import React, { useState } from "react";
import {
  MdBlock,
  MdLocalShipping,
  MdOutlineInventory2,
  MdOutlineStorefront,
} from "react-icons/md";
import HyperLink from "../../../../../../../Components/HyperLink/HyperLink";
import StatusIcons from "../../../../../../../Components/StatusIcons/StatusIcons";
import ShipmentPackagesModal from "../../../../../../../Components/Shared/Shipment/ShipmentPackages/ShipmentPackagesModal";
import { formatMoney } from "./money";

const MAX_TICKS = 4;

const lower = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

// Same buckets as the shared ShipmentPackagesPreview: creation, delivered
// and cancelled; anything else is still moving. Delivered packages go last.
const packageState = (status) => {
  const value = lower(status);
  if (value === "creation") return "created";
  if (value === "delivered") return "delivered";
  if (value === "cancelled" || value === "canceled") return "cancelled";
  return "moving";
};

const TICKS = {
  delivered: {
    label: "Delivered",
    mark: "✓",
    className: "bg-state-success-bg text-state-success-text",
  },
  cancelled: {
    label: "Cancelled",
    mark: "×",
    className: "bg-state-danger-bg text-state-danger-text",
  },
  moving: {
    label: "Processing",
    mark: "…",
    className: "bg-brand-light text-brand",
  },
  created: { label: "Label Created", mark: "", className: "bg-ui-border" },
};

// Carrier wording, tidied for display only (filters keep the raw values;
// the hover shows the carrier's own words).
const CARRIER_WORDING = [
  [/^(creation|label created)$/, "Label created"],
  [/^shipment ready for/, "Ready for pickup"],
  [/^(out for delivery|on delivery)/, "Out for delivery"],
  [/^on the way$/, "On the way"],
  [/^in transit$/, "In transit"],
  [/^clearance in progress$/, "Clearance in progress"],
];

const shipmentStatusLabel = (status) => {
  const value = lower(status);
  return (
    CARRIER_WORDING.find(([pattern]) => pattern.test(value))?.[1] ?? status
  );
};

const formatWeight = (weight, unit) =>
  `${weight >= 10 ? Math.round(weight) : Math.round(weight * 10) / 10} ${unit}`;

// "Covered" when the order's shipping charge is at least 95% of what the
// shipment cost (within 5%, or more); below that the order loses money.
const COVERED_SHARE = 0.95;

const toNumber = (value) =>
  value == null || value === ""
    ? null
    : Number.isFinite(Number(value))
      ? Number(value)
      : null;

/**
 * Right end of line 2, in words: "Charged $50.00 · Cost $262.30".
 * Charged = the shipping charged to the customer (the order's shipping
 * total); Cost = what the shipment cost (its Bolt Total, tax included).
 * The cost is green when the charge covers it, red when the order loses
 * money on shipping; compared only when the order is in CAD, as costs are.
 * Either amount shows alone when the other doesn't exist; a missing charge
 * means it isn't recorded (e.g. orders not from a marketplace), not $0.
 */
const Amount = ({ row, cost }) => {
  const charged = toNumber(row?.shipping_total);
  const currency = String(row?.currency ?? "")
    .trim()
    .toUpperCase();
  const inCad = !currency || currency === "CAD";
  const chargedText =
    charged == null
      ? null
      : inCad
        ? formatMoney(charged)
        : `${currency} ${charged.toFixed(2)}`;

  // A recorded charge shows even at $0.00; only a missing one is left out.
  const hasCharge = charged != null;
  if (!hasCharge && cost == null) return null;

  const costText = cost == null ? null : formatMoney(cost);
  const compared = hasCharge && cost != null && inCad;
  const covered = compared && charged >= cost * COVERED_SHARE;
  const difference = compared ? charged - cost : 0;

  const result = !compared
    ? null
    : difference >= 0
      ? `Profit on shipping: $${difference.toFixed(2)}`
      : covered
        ? `Within 5% of the cost: −$${Math.abs(difference).toFixed(2)}`
        : `Loss on shipping: $${Math.abs(difference).toFixed(2)}`;
  const title = [
    hasCharge
      ? `Shipping charged to customer: ${chargedText}`
      : "Shipping charged to customer: not recorded on this order",
    cost != null && `Shipping cost (Bolt, incl. tax): ${costText}`,
    result,
    hasCharge &&
      cost != null &&
      !inCad &&
      `Not compared: the order is in ${currency}, the cost in CAD`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <span
      title={title}
      className="flex shrink-0 items-center gap-[3px] font-normal text-ui-text-subtle"
    >
      {hasCharge && (
        <>
          <span>Charged</span>
          <span className="font-medium text-ui-text-muted">{chargedText}</span>
        </>
      )}
      {hasCharge && cost != null && (
        <span aria-hidden="true" className="px-[2px]">
          ·
        </span>
      )}
      {cost != null && (
        <>
          <span>Cost</span>
          <span
            className={`font-medium ${
              !compared
                ? "text-ui-text-muted"
                : covered
                  ? "text-state-success-text"
                  : "text-state-danger-text"
            }`}
          >
            {costText}
          </span>
        </>
      )}
    </span>
  );
};

// The carrier of a Bolt shipment or a manual one: logo when there is one,
// else its name (shown as a short code in the tile).
const carrierOf = (row) => {
  const company = row?.shipment
    ? row.shipment.shipping_company?.shipping_company
    : row?.manual_shipment?.shipping_company?.shipping_company;
  const name =
    company?.name ??
    row?.manual_shipment?.manual_shipping_company?.shipping_company_name ??
    "";
  return { logo: company?.logo_url ?? null, name: String(name).trim() };
};

const Tile = ({ title, children, white = false }) => (
  <span
    title={title}
    aria-hidden="true"
    className={`flex h-[24px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] border-[1px] border-ui-border ${
      white ? "bg-[#fff]" : "bg-ui-surface text-ui-text-subtle"
    }`}
  >
    {children}
  </span>
);

const Layout = ({ icon, line1, line2, amount, line2Title }) => (
  <div className="flex w-full min-w-0 items-center gap-[8px]">
    {icon}
    <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
      <div className="flex min-w-0 items-center gap-[6px]">{line1}</div>
      <div
        title={line2Title}
        className="flex min-w-0 items-center gap-[6px] font-normal text-ui-text-subtle"
      >
        {/* Clipped so packages and reference never run into the amount. */}
        <div className="flex min-w-0 flex-1 items-center gap-[4px] overflow-hidden">
          {line2}
        </div>
        {amount}
      </div>
    </div>
  </div>
);

/**
 * Shipping column: carrier logo, the tracking number (linked to Bolt
 * tracking, as before) and the shipment status; underneath, the packages
 * with a tick each (click opens them), the reference when it isn't the
 * order number, and the shipping charged against the shipment cost. Orders
 * without a shipment say how they go out instead (pickup, self delivery,
 * not shipped); "Not shipped" offers Create shipment on row hover, the same
 * action as the menu's Ship Order.
 * Warehouse Orders passes showAmount={false} (the shipper's charge and cost
 * stay private) and `actions` (its Label / Decl downloads), shown at the
 * right end of line 2.
 * Shipper Orders passes `routeControl` (the route dropdown), shown on line 2
 * of a self-delivery order without a shipment.
 */
const ShippingCell = ({
  row,
  onCreateShipment,
  showAmount = true,
  actions,
  routeControl,
}) => {
  const [packagesModal, setPackagesModal] = useState(false);
  const shipment = row?.shipment;
  const manual = row?.manual_shipment;
  const endOfLine2 = (amount) => (
    <>
      {showAmount && amount}
      {actions}
    </>
  );

  if (!shipment && !manual) {
    const type = lower(row?.type);
    const cancelled = lower(row?.status) === "cancelled";
    const [Icon, text, detail] =
      type === "self pickup"
        ? [
            MdOutlineStorefront,
            "Customer pickup",
            row?.warehouse?.name ? `At ${row.warehouse.name}` : "",
          ]
        : type === "self delivery"
          ? [MdLocalShipping, "Self delivery", ""]
          : cancelled
            ? [MdBlock, "Never shipped", ""]
            : [MdOutlineInventory2, "Not shipped", ""];
    const notShipped = text === "Not shipped";
    const selfDelivery = text === "Self delivery";

    return (
      <Layout
        icon={
          <Tile>
            <Icon className="h-[14px] w-[14px]" />
          </Tile>
        }
        line1={
          <span className="min-w-0 truncate font-normal text-ui-text-muted">
            {text}
          </span>
        }
        line2={
          detail ? (
            <span className="min-w-0 truncate">{detail}</span>
          ) : notShipped && onCreateShipment ? (
            <button
              type="button"
              onClick={() => onCreateShipment(row)}
              aria-label={`Create a shipment for #${row?.id}`}
              className="orders-hover-reveal min-w-0 truncate border-0 bg-transparent p-0 font-medium text-brand hover:underline"
            >
              Create shipment
            </button>
          ) : selfDelivery && routeControl ? (
            routeControl
          ) : null
        }
        amount={endOfLine2(<Amount row={row} cost={null} />)}
      />
    );
  }

  const carrier = carrierOf(row);
  const tracking = (shipment ?? manual)?.shipment_tracking_id;
  const status = (shipment ?? manual)?.shipment_status;
  const reference = shipment?.referance_code;
  const showReference = reference && lower(reference) !== lower(row?.order_id);

  const unit = shipment?.unit === "Imperial" ? "lb" : "kg";
  const packages = [...(shipment?.shipment_details ?? [])].sort(
    (a, b) =>
      Number(packageState(a?.tracking_status) === "delivered") -
      Number(packageState(b?.tracking_status) === "delivered"),
  );
  const weight = packages.reduce(
    (sum, pkg) => sum + (parseFloat(pkg?.weight) || 0),
    0,
  );
  const packagesTitle = packages
    .map((pkg, index) =>
      [
        `Package ${index + 1}: ${pkg?.weight ?? "N/A"} ${unit}`,
        pkg?.tracking_status || "N/A",
        pkg?.description,
      ]
        .filter(Boolean)
        .join(" · "),
    )
    .join("\n");

  const line2Title = [
    carrier.name,
    tracking,
    reference && `Reference ${reference}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      {shipment && (
        <ShipmentPackagesModal
          open={packagesModal}
          shipmentId={shipment.id}
          shipmentTracking={shipment.shipment_tracking_id}
          unit={shipment.unit}
          onClose={() => setPackagesModal(false)}
        />
      )}
      <Layout
        icon={
          carrier.logo ? (
            <Tile title={carrier.name} white={true}>
              <img
                src={carrier.logo}
                alt=""
                loading="lazy"
                className="h-full w-full object-contain p-[2px]"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </Tile>
          ) : (
            <Tile title={carrier.name}>
              <span className="text-[9px] font-bold uppercase">
                {carrier.name.replace(/[^A-Za-z]/g, "").slice(0, 3) || "—"}
              </span>
            </Tile>
          )
        }
        line1={
          <>
            {/* The tracking number keeps its room; a long status shortens. */}
            <span className="min-w-0 max-w-[75%] shrink-0 truncate">
              {tracking ? (
                <HyperLink code={tracking} />
              ) : (
                <span className="font-normal text-ui-text-muted">
                  {carrier.name || "No tracking yet"}
                </span>
              )}
            </span>
            {status && (
              <span
                title={`Carrier status: ${status}`}
                className="ml-auto flex min-w-0 justify-end"
              >
                <StatusIcons
                  status={shipmentStatusLabel(status)}
                  noBg={true}
                  noDot={true}
                />
              </span>
            )}
          </>
        }
        line2Title={line2Title}
        line2={
          <>
            {/* Carriers without a logo (e.g. manual "ICS Courier") show
                their full name; line 1 already shows it when there is no
                tracking number. */}
            {!carrier.logo && carrier.name && tracking && (
              <span className="min-w-0 shrink-0 truncate font-medium text-ui-text-muted">
                {carrier.name}
                {(packages.length > 0 || showReference) && " ·"}
              </span>
            )}
            {packages.length > 0 && (
              <button
                type="button"
                title={packagesTitle}
                onClick={() => setPackagesModal(true)}
                className="flex min-w-0 items-center gap-[4px] border-0 bg-transparent p-0 text-left text-inherit hover:text-brand"
              >
                <span className="shrink-0">
                  {packages.length} {packages.length === 1 ? "pkg" : "pkgs"}
                  {weight > 0 && ` · ${formatWeight(weight, unit)}`}
                </span>
                {/* Ticks that don't fit wrap onto a hidden second line, so
                    one is never cut in half next to the amounts. */}
                <span className="flex h-[12px] min-w-0 flex-wrap items-center gap-[2px] overflow-hidden">
                  {packages.slice(0, MAX_TICKS).map((pkg, index) => {
                    const tick = TICKS[packageState(pkg?.tracking_status)];
                    return (
                      <span
                        key={pkg?.id ?? index}
                        aria-label={tick.label}
                        className={`flex h-[12px] w-[12px] shrink-0 items-center justify-center rounded-full text-[8px] font-bold leading-none ${tick.className} ${
                          tick.mark ? "" : "scale-[0.66]"
                        }`}
                      >
                        {tick.mark}
                      </span>
                    );
                  })}
                  {packages.length > MAX_TICKS && (
                    <span className="shrink-0 text-[9px] leading-[12px]">
                      +{packages.length - MAX_TICKS}
                    </span>
                  )}
                </span>
              </button>
            )}
            {showReference && (
              <span className="min-w-0 truncate">
                {packages.length > 0 && "· "}Ref {reference}
              </span>
            )}
          </>
        }
        amount={endOfLine2(
          // Manual shipments store no cost: the charge shows alone.
          <Amount
            row={row}
            cost={shipment ? toNumber(shipment.Total) : null}
          />,
        )}
      />
    </>
  );
};

export default ShippingCell;
